import type { PublishedCard } from "@/cards/common/types";
import {
  getMainCardContent,
  normalizeMainCardTypeKey,
} from "@/cards/common/utils/app-deck/mainCardShellReaders";

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isSyntheticDemoPageType = (type?: string) => {
  const key = normalizeMainCardTypeKey(type);
  return (
    key === "demo_card_exps" ||
    key === "demo_card_exp_list" ||
    key === "demo_card_exp_creation" ||
    key === "demo_card_normal_exps"
  );
};

/** True when release_date is in the past (or missing). Status alone does not block. */
export const isDemoEpisodeReleased = (exp?: Record<string, unknown> | null): boolean => {
  if (!exp) return false;

  const raw = exp.release_date;
  if (raw == null || raw === "") return true;
  const d = new Date(typeof raw === "number" ? raw : String(raw));
  if (Number.isNaN(d.getTime())) return true;
  return d.getTime() <= Date.now();
};

/** Episode rows live on top-level `content.exps` (fallback: cover page content.exps). */
export const findDemoCardEmbeddedExps = (
  ...sources: Array<Record<string, unknown> | PublishedCard | null | undefined>
): Record<string, unknown>[] => {
  const byId = new Map<string, Record<string, unknown>>();

  const ingest = (rows: unknown[]) => {
    for (const row of rows) {
      if (!isObject(row)) continue;
      const id = row.id != null ? String(row.id) : JSON.stringify(row);
      byId.set(id, row);
    }
  };

  for (const source of sources) {
    if (!source) continue;

    if (Array.isArray((source as { exps?: unknown }).exps)) {
      ingest((source as { exps: unknown[] }).exps);
    }

    const topContent = getMainCardContent(source as Record<string, unknown>);
    if (Array.isArray(topContent.exps)) {
      ingest(topContent.exps as unknown[]);
    }

    if (isObject((source as { content?: unknown }).content)) {
      const content = (source as { content: Record<string, unknown> }).content;
      if (Array.isArray(content.exps)) ingest(content.exps as unknown[]);
    }

    const pages = Array.isArray((source as { pages?: unknown }).pages)
      ? (source as { pages: Array<{ type?: string; content?: unknown }> }).pages
      : [];
    for (const page of pages) {
      if (!isObject(page?.content)) continue;
      // Prefer nested content.exps on cover; also treat demo_card_exps page content as a row.
      if (Array.isArray(page.content.exps)) {
        ingest(page.content.exps as unknown[]);
      }
      const pageType = normalizeMainCardTypeKey(String(page.type ?? ""));
      if (pageType === "demo_card_exps") {
        ingest([page.content]);
      }
    }
  }

  return Array.from(byId.values());
};

/**
 * Builds book pages for demo episodes from `content.exps`:
 * - `demo_card_exp_list` includes every episode (released + upcoming)
 * - `demo_card_exps` pages: talent/owner sees all; fans only released
 * - Fans without `purchase` get neither list nor episode pages
 * - Talent preview can force include via `includeEpisodePages`
 */
export const enhanceDemoCardForPreview = (
  card: PublishedCard | null,
  options?: {
    exps?: Record<string, unknown>[];
    includeEpisodePages?: boolean;
    isTalentView?: boolean;
  }
): PublishedCard | null => {
  if (!card) return null;

  const pages = Array.isArray(card.pages) ? card.pages : [];
  // Drop any existing episode pages — we'll rebuild from content.exps.
  const basePages = pages.filter((page) => !isSyntheticDemoPageType(page?.type));

  const isPurchased = (card as { purchase?: unknown }).purchase != null;
  const isTalent = Boolean(options?.includeEpisodePages || options?.isTalentView);
  if (!isPurchased && !isTalent) {
    return {
      ...card,
      pages: basePages as PublishedCard["pages"],
    };
  }

  const allExps =
    options?.exps && options.exps.length > 0
      ? options.exps
      : findDemoCardEmbeddedExps(card as unknown as Record<string, unknown>);

  if (allExps.length === 0) {
    return {
      ...card,
      pages: basePages as PublishedCard["pages"],
    };
  }

  // Listing: all episodes. Book pages: talent sees unreleased too; fans only released.
  const pageExps = isTalent ? allExps : allExps.filter((exp) => isDemoEpisodeReleased(exp));

  const maxOrder = Math.max(
    0,
    ...basePages.map((page) => Number((page as { order?: number })?.order ?? 0))
  );

  const listPage = {
    order: maxOrder + 0.05,
    type: "demo_card_exp_list",
    content: { allEpisodes: allExps },
    assets: [] as number[],
  };

  const expPages = pageExps.map((exp, index) => ({
    order: maxOrder + (index + 1) * 0.1,
    type: "demo_card_exps",
    content: exp,
    assets: [] as number[],
  }));

  return {
    ...card,
    pages: [...basePages, listPage, ...expPages] as PublishedCard["pages"],
  };
};
